import Apertavel from '@/components/movimento/Apertavel';

export type TelaEquipe = 'pessoas' | 'montagem' | 'formacoes' | 'relatorios';

interface Props {
  comVaga: number;
  /** Suplentes (lista de espera) nunca entram nos números grandes. */
  suplentes: number;
  /** Quantas pessoas da equipe ainda estão sem equipe na montagem. */
  semEquipe: number;
  /** Data da próxima formação (ISO) ou null. */
  proximaFormacao: string | null;
  aoAbrir: (tela: TelaEquipe) => void;
}

const CORES: Record<'primary' | 'accent' | 'secondary', string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-900',
  secondary: 'bg-secondary-100 text-secondary-900',
};

interface Cartao {
  tela: TelaEquipe;
  icone: string;
  titulo: string;
  descricao: string;
  numero: string;
  legenda: string;
  cor: 'primary' | 'accent' | 'secondary';
}

function diaMes(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  return `${dia}/${mes}`;
}

// Hub da aba Equipe: um cartão grande por função, sem subabas.
export default function HubEquipe({
  comVaga,
  semEquipe,
  proximaFormacao,
  suplentes,
  aoAbrir,
}: Props) {
  // O número grande é SEMPRE quem tem vaga; suplentes só aparecem na descrição.
  const descricaoPessoas =
    suplentes > 0
      ? `${comVaga} com vaga · ${suplentes} ${suplentes === 1 ? 'suplente' : 'suplentes'}`
      : `${comVaga} com vaga`;

  const cartoes: Cartao[] = [
    {
      tela: 'pessoas',
      icone: 'ri-group-line',
      titulo: 'Pessoas',
      descricao: descricaoPessoas,
      numero: String(comVaga),
      legenda: 'com vaga',
      cor: 'primary',
    },
    {
      tela: 'montagem',
      icone: 'ri-layout-grid-line',
      titulo: 'Montagem das equipes',
      descricao:
        semEquipe > 0 ? 'Quem ainda está sem equipe' : 'Todo mundo já tem equipe',
      numero: String(semEquipe),
      legenda: 'sem equipe',
      cor: 'accent',
    },
    {
      tela: 'formacoes',
      icone: 'ri-qr-scan-2-line',
      titulo: 'Formações e presenças',
      descricao: 'Presença por QR em cada formação',
      numero: diaMes(proximaFormacao),
      legenda: proximaFormacao ? 'próxima' : 'nenhuma marcada',
      cor: 'secondary',
    },
    {
      tela: 'relatorios',
      icone: 'ri-file-list-3-line',
      titulo: 'Relatórios da equipe',
      descricao: descricaoPessoas,
      numero: String(comVaga),
      legenda: 'com vaga',
      cor: 'primary',
    },
  ];

  return (
    <div className="space-y-2.5 lg:grid lg:grid-cols-3 lg:gap-3 lg:space-y-0">
      {cartoes.map((c) => (
        <Apertavel
          key={c.tela}
          onClick={() => aoAbrir(c.tela)}
          className="flex min-h-[84px] w-full cursor-pointer items-center gap-3 rounded-3xl border border-background-200 bg-white px-4 py-3.5 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/40"
        >
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-xl ${CORES[c.cor]}`}
          >
            <i className={c.icone} aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-bold text-foreground-950">
              {c.titulo}
            </span>
            <span className="mt-0.5 block text-[12px] leading-snug text-foreground-600">
              {c.descricao}
            </span>
          </span>
          <span className="shrink-0 text-right">
            <span className="block text-[18px] font-extrabold leading-none tabular-nums text-foreground-950">
              {c.numero}
            </span>
            <span className="mt-1 block whitespace-nowrap text-[11px] font-medium text-foreground-500">
              {c.legenda}
            </span>
          </span>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-400">
            <i className="ri-arrow-right-s-line text-lg" aria-hidden />
          </span>
        </Apertavel>
      ))}
    </div>
  );
}